import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Check } from "lucide-react";

export default function KonkurranseEnter() {
  const { id } = useParams();
  const nav = useNavigate();
  const [contest, setContest] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/contests/${id}`).then(async (r) => {
      const c = r.data.contest;
      setContest(c);
      try {
        if (c.type === "novelle") {
          const me = await api.get("/me");
          const nv = await api.get(`/users/${me.data.username}/noveller`);
          setItems((nv.data?.results || []).map((n) => ({ id: n.id, label: n.title, sub: n.teaser?.slice(0, 80) })));
        } else {
          const mt = c.type === "video" ? "video" : c.type === "image" ? "image" : "all";
          const gal = await api.get(`/me/images?limit=200&media_type=${mt}`);
          const rows = gal.data || [];
          setItems(rows.map((x) => ({
            id: x.id, label: x.caption || "Uten tittel",
            thumb: x.thumbnail_path || x.url, media_type: x.media_type
          })));
        }
      } catch (e) { setItems([]); }
    }).finally(() => setLoading(false));
  }, [id]);

  const submit = async () => {
    if (!selected && contest?.type !== "creator") {
      toast.error("Velg et bidrag først");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/contests/${id}/enter`, { ref_id: selected });
      toast.success("Bidrag meldt inn!");
      nav(`/konkurranser/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke melde inn");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Layout><div className="text-center text-[var(--cream-soft)] py-20">Laster…</div></Layout>;
  if (!contest) return <Layout><div className="text-center py-20">Ikke funnet.</div></Layout>;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <Link to={`/konkurranser/${id}`} className="text-sm text-[var(--cream-soft)] hover:text-[var(--cream)]">← Tilbake</Link>
        <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mt-3">Meld inn bidrag</h1>
        <p className="text-[var(--cream-soft)] text-sm mb-8">Velg fra dine egne {contest.type === "novelle" ? "noveller" : contest.type === "video" ? "videoer" : "bilder"}.</p>

        {items.length === 0 ? (
          <div className="panel-tight p-12 text-center">
            <p className="text-[var(--cream-soft)]">Du har ikke noe passende {contest.type === "novelle" ? "novelle" : "media"} enda.</p>
            <Link to={contest.type === "novelle" ? "/noveller/ny" : "/bilder"} className="text-[var(--magenta)] underline">Last opp først</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {items.map((it) => (
              <button
                key={it.id}
                onClick={() => setSelected(it.id === selected ? null : it.id)}
                data-testid={`pick-${it.id}`}
                className={`panel-card text-left transition relative ${selected === it.id ? "ring-2 ring-[var(--magenta)]" : "hover:border-[var(--magenta)]/40"}`}
              >
                {it.thumb ? (
                  <div className="aspect-[3/4] bg-[var(--noir-elev)] overflow-hidden">
                    <img src={it.thumb} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="aspect-[3/4] bg-[var(--noir-elev)] p-3 flex items-end">
                    <div className="font-fraunces italic text-[var(--cream)]">{it.label}</div>
                  </div>
                )}
                <div className="p-2 text-xs text-[var(--cream-soft)] truncate">{it.label}</div>
                {selected === it.id && (
                  <div className="absolute top-2 right-2 w-7 h-7 bg-[var(--magenta)] text-black flex items-center justify-center">
                    <Check className="h-4 w-4" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="mt-8 flex justify-end">
          <button
            onClick={submit}
            disabled={(!selected && contest.type !== "creator") || submitting}
            data-testid="submit-entry-btn"
            className="px-6 py-3 bg-[var(--magenta)] text-black font-meta disabled:opacity-50"
          >
            {submitting ? "SENDER…" : "MELD INN BIDRAG"}
          </button>
        </div>
      </div>
    </Layout>
  );
}
